// Rule-based proactive alert helpers derived from training data.
// These produce no AI calls — all logic is deterministic.

import { getSportProfile } from './sportProfile';

const k42 = 1 - Math.exp(-1 / 42);
const k7  = 1 - Math.exp(-1 / 7);

// Return YYYY-MM-DD in local time — avoids UTC drift when mixing
// new Date() with toISOString() (which is always UTC).
function localDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function computeATLCTL(recentWorkouts) {
  const tssByDay = {};
  recentWorkouts.forEach(w => {
    const d = w.date?.slice(0, 10);
    if (d) tssByDay[d] = (tssByDay[d] || 0) + (w.tss || 0);
  });
  const today = new Date();
  let ctl = 0, atl = 0;
  // Warm up over 180 days — same window as DashboardPage — so ACWR here
  // matches the Dashboard gauge exactly. Starting from only 42 days produced
  // an artificially low CTL (EWA not converged) and therefore inflated ACWR.
  for (let i = 179; i >= 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    const tss = tssByDay[localDateStr(d)] || 0;
    ctl = ctl + (tss - ctl) * k42;
    atl = atl + (tss - atl) * k7;
  }
  return { ctl, atl };
}

/**
 * Returns an alert when ACWR is outside the safe zone (0.6–1.3).
 * Returns null if the athlete is in the optimal range or has insufficient data.
 *
 * @param   {Array}  recentWorkouts  Full workout array
 * @returns {null | { level: string, message: string, suggestion: string }}
 */
export function computeACWRAlert(recentWorkouts = []) {
  const { ctl, atl } = computeATLCTL(recentWorkouts);
  if (ctl < 1) return null;
  const acwr = +(atl / ctl).toFixed(2);

  if (acwr > 1.5) {
    return {
      level: 'high-risk',
      message: `Your acute load is well above your chronic fitness (ACWR ${acwr}). This is the High Risk zone — injury likelihood is significantly elevated. A recovery session or rest day today is strongly recommended.`,
      suggestion: `My ACWR is at ${acwr} — what should I do to avoid overreach injury?`,
    };
  }
  if (acwr > 1.3) {
    return {
      level: 'caution',
      message: `Training load is trending high this week (ACWR ${acwr} — Caution zone). You can keep training, but prioritise sleep and nutrition to absorb the workload.`,
      suggestion: `My workload ratio is ${acwr} — should I ease off or is this okay?`,
    };
  }
  if (acwr < 0.6 && ctl > 5) {
    return {
      level: 'undertraining',
      message: `Training load has dropped significantly (ACWR ${acwr}). If this is an intentional recovery week, great. Otherwise, consider building back gradually to protect your fitness base.`,
      suggestion: `My training load has dropped a lot — how do I build back without losing fitness?`,
    };
  }
  return null;
}

/**
 * Returns the most-neglected discipline if any is missing for 14+ days,
 * filtered to only the disciplines relevant to the athlete's sportFocus.
 *
 * @param   {Array}  recentWorkouts  Full workout array
 * @param   {string} sportFocus      e.g. 'triathlon', 'run', 'bike', 'swim', 'duathlon', 'aquabike'
 * @returns {null | { discipline: string, daysSince: number | null }}
 */
export function computeDisciplineGap(recentWorkouts = [], sportFocus = 'triathlon') {
  const profile = getSportProfile(sportFocus);
  // Only check disciplines relevant to this athlete's sport
  const DISCIPLINES = ['swim', 'bike', 'run'].filter(d => profile[d]);

  // Single-sport athletes — gap detection is not meaningful (they only do one thing)
  if (DISCIPLINES.length <= 1) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const lastSeen = {};
  recentWorkouts.forEach(w => {
    const type = (w.type || '').toLowerCase();
    if (!DISCIPLINES.includes(type)) return;
    const d = new Date(w.date);
    if (!lastSeen[type] || d > lastSeen[type]) lastSeen[type] = d;
  });

  let worst = null;
  let maxDays = 13; // threshold: strictly more than 13 days = 14+
  DISCIPLINES.forEach(type => {
    if (!lastSeen[type] && recentWorkouts.length >= 10) {
      // Enough training history but this discipline has never appeared
      if (!worst) worst = { discipline: type, daysSince: null };
    } else if (lastSeen[type]) {
      const days = Math.round((now - lastSeen[type]) / 86400000);
      if (days > maxDays) {
        worst = { discipline: type, daysSince: days };
        maxDays = days;
      }
    }
  });
  return worst;
}
